import type {GeoRefAddress, GeoRefPrecision} from "@reva/shared/geo-reference";

import type ServiceApi from "../../api";
import {ErrorI18n} from "../../i18n";
import {AvmApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import {ValidationErrorI18n} from "../validation";
import {WebError} from "../web-error";

/** Präzisionen, mit denen REVA bewertet; interpoliert liegt zwischen HOUSE und STREET_SECTION (#19748). */
const PRECISE_ENOUGH: readonly GeoRefPrecision[] = [
    "COORDINATE",
    "HOUSE",
    "HOUSENUMBER_INTERPOLATED",
    "STREET_SECTION",
];

/** A GeoRef is precise enough if it has a street and one of the {@link PRECISE_ENOUGH} precisions. */
export function isPreciseEnough(addressMeta?: GeoRefAddress): boolean {
    return !!(addressMeta?.precision && addressMeta.street && PRECISE_ENOUGH.includes(addressMeta.precision));
}

/**
 * Result of a GeoRef query: either a single distinct address, or multiple ambiguous candidates.
 */
export type GeoRefResult =
    | {ambiguous: false; addressMeta: GeoRefAddress}
    | {ambiguous: true; addresses: GeoRefAddress[]};

/**
 * API interface for the AVM GeoRef request (GET {@link AvmApi.PATH_GEOREF}).
 *
 * Unlike most endpoints, an HTTP 409 is NOT an error here: it signals an ambiguous query and carries the
 * matching addresses. Results are additionally filtered by precision; an ambiguous list can collapse to a
 * single distinct address, or be rejected entirely with "address.error.not_precise_enough".
 */
export class GeoRefApiInterface extends SimpleApiInterface<GeoRefResult> {
    private readonly address: string;
    private readonly accounting?: string | null;
    private readonly cache?: boolean;
    private readonly signal?: AbortSignal;

    /**
     * @param address requested address
     * @param accounting optional accounting flag
     * @param cache optional cache flag; if set to false, the cache will be deleted
     * @param signal optionales AbortSignal zum Abbrechen der Anfrage
     */
    constructor(address: string, accounting?: string | null, cache?: boolean, signal?: AbortSignal) {
        super();
        this.address = address;
        this.accounting = accounting;
        this.cache = cache;
        this.signal = signal;
    }

    protected validate(): void {
        if (!this.address || this.address.length < 4) {
            throw new ValidationErrorI18n("address", "address.error.too_short");
        }
        if (this.address.match(/(i?)^POINT\([^()]*?\)|^\d\d?[.,]\d+\/\d\d?[.,]\d+$/)) {
            throw new ValidationErrorI18n("address", "address.error.address_required");
        }
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildAvmApiUrl([AvmApi.PATH_GEOREF], {
            address: this.address,
            accounting: this.accounting ?? undefined,
            cache: this.cache === false ? "false" : undefined,
        });
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<GeoRefResult> {
        const single = (await executor.fetchGET(url, credentials, this.signal)) as GeoRefAddress;
        return this.requirePrecise(single);
    }

    protected handleApiErrors(e: Error): GeoRefResult {
        // A 409 is not an error here: the query was ambiguous and the matching addresses are returned.
        if (e instanceof WebError && e.status === 409) {
            return this.resolveAmbiguous((e.json?.geoRefs ?? []) as GeoRefAddress[]);
        }
        // Preserve domain errors (e.g. "not_precise_enough" thrown during response evaluation) instead of
        // letting the base handler wrap them into "general.error.invalid".
        if (e instanceof ErrorI18n) {
            throw e;
        }
        return super.handleApiErrors(e);
    }

    /** Require the single (non-ambiguous) result to be precise enough, otherwise reject. */
    private requirePrecise(single: GeoRefAddress): GeoRefResult {
        if (!isPreciseEnough(single)) {
            throw new ErrorI18n("address.error.not_precise_enough");
        }
        return {ambiguous: false, addressMeta: single};
    }

    /** Filter the ambiguous candidates by precision; collapse to a single result or reject if none remain. */
    private resolveAmbiguous(addresses: GeoRefAddress[]): GeoRefResult {
        const preciseAddresses = addresses.filter((address) => isPreciseEnough(address));
        if (preciseAddresses.length === 0) {
            throw new ErrorI18n("address.error.not_precise_enough");
        }
        if (preciseAddresses.length === 1) {
            return {ambiguous: false, addressMeta: preciseAddresses[0]};
        }
        return {ambiguous: true, addresses: preciseAddresses};
    }
}
